import React, { useState } from 'react';
import { Box, Button, Flex, useDisclosure, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GridStatus } from 'clipsal-cortex-types/src/api/api-saturn-live-data';
import { AlertBanner } from 'clipsal-cortex-ui/src/components/AlertBanner';

import { OptionSelectAlertDrawer } from '../../common/components/OptionSelectAlertDrawer';
import { SlidingAnimationPageBase } from '../../common/components/SlidingAnimationPageBase';
import { IS_DEMO_LOGIN, IS_WEB } from '../../common/constants';
import { useSiteCapabilityCheck } from '../../common/hooks/use-site-capability-check';
import { GridOutage } from '../home/default-home/GridOutage';
import { useLiveData } from '../site/live-data/liveDataApi';
import { AylaDevicesList } from './ayla-switches/AylaDevicesList';
import { DeviceListAccordion } from './DeviceListAccordion';
import { MANUFACTURER_ID_AYLA } from './devices-helper';
import { DevicesList } from './DevicesList';
import { SortByOptions, SortByPopover } from './SortByPopover';
import { useGetSwitchesByManufacturerQuery } from './switches/switchApi';
import { SwitchesList } from './switches/SwitchesList';

export function DevicesHome() {
  const { isError } = useGetSwitchesByManufacturerQuery([MANUFACTURER_ID_AYLA]);
  const { siteHasMeter, siteHasAylaSwitches } = useSiteCapabilityCheck();
  const {
    data: { grid_status: gridStatus },
  } = useLiveData();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast({ isClosable: true });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [sortBy, setSortBy] = useState<SortByOptions>('COST');

  const isPowerOutage = gridStatus === GridStatus.GRID_OUTAGE;

  async function handleChangeSortBy(sortBy: SortByOptions) {
    setSortBy(sortBy);
  }

  function handleSelectEditDrawerOption(value: string) {
    if (value === 'ADD_DEVICE') {
      /*istanbul ignore else -- @preserve*/
      if (IS_WEB) {
        toast({
          title: t('Common.unavailable on web'),
          description: t('Common.download app to use feature'),
          status: 'error',
        });
      } else {
        navigate(`../add`);
      }
    }
    onClose();
  }

  return (
    <SlidingAnimationPageBase
      includeTopNavHamButton
      includeSchneiderHamButton
      includeBottomNav
      title={t('Devices.devices')}
    >
      <Box w="100%" px={3}>
        <Flex align="center" justify="space-between" pb={1}>
          <SortByPopover sortBy={sortBy} onChangeSortBy={handleChangeSortBy} />
          <OptionSelectAlertDrawer
            isOpen={isOpen}
            onClose={onClose}
            onSelectOption={handleSelectEditDrawerOption}
            options={[
              {
                value: 'ADD_DEVICE',
                label: t('Devices.add device'),
              },
            ]}
            title={t('Devices.customize rooms')}
          />
          <Button
            data-testid="devices-edit-button"
            variant={'ghost'}
            size={'sm'}
            isDisabled={IS_DEMO_LOGIN}
            colorScheme={'schneiderSkyBlue'}
            fontWeight={500}
            onClick={onOpen}
          >
            {t('Common.edit')}
          </Button>
        </Flex>
        {isPowerOutage && <GridOutage message={t('Widgets.Grid Outage.message devices')} />}
        {isError && (
          <AlertBanner
            title={t('Devices.error fetching devices')}
            type="error"
            isCollapsible={false}
            isDismissable={false}
            containerTestId="error-alert"
          >
            {t('Common.please try again')} {t('Common.if this persists contact support')}
          </AlertBanner>
        )}
        {siteHasMeter && (
          <DeviceListAccordion title={t('Devices.smart circuits').toUpperCase()}>
            <SwitchesList sortBy={sortBy} />
          </DeviceListAccordion>
        )}
        {siteHasAylaSwitches && (
          <DeviceListAccordion title={t('Devices.smart devices').toUpperCase()}>
            <AylaDevicesList sortBy={sortBy} />
          </DeviceListAccordion>
        )}
        {siteHasMeter && (
          <DeviceListAccordion title={t('Devices.discovered devices').toUpperCase()}>
            <DevicesList sortBy={sortBy} />
          </DeviceListAccordion>
        )}
      </Box>
    </SlidingAnimationPageBase>
  );
}
